import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonListHeader,
  IonPage,
  IonSpinner,
  IonToolbar,
  useIonAlert,
  useIonToast,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import './css/Profile.css';

import DebugModal from '../../components/DebugModal';
import Toolbar from './components/Toolbar';

import useCounter from 'hooks/useCounter';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { getSnapshot, SnapshotOut } from 'mobx-state-tree';
import { UserSN } from 'models/User';
import {
  KeyStatusResponse,
  useCalculationsAPI,
} from 'services/freak_estimator/Estimator';
import { useFSM } from 'services/DongleController/DongleController';
import { useStores } from '../../models';
import RiderEditModal from '../../components/modals/RiderEditModal';
import BoardEditModal from '../../components/modals/BoardEditModal';
import SettingsModal from '../../components/modals/SettingsModal';
import { isInDebugMode } from '../../util';
import useFirmwareUpdate from 'hooks/useFirmwareUpdate';

// Import RunList components
import { RunModel } from '../../models/RunModel/RunModel';
import RunListItem from '../../components/listElements/RunListItem/RunListItem';
import { useCloudStorage } from 'services/Database/CloudStorageService';
import FavoritesCarousel from 'pages/PublicRunsPage/components/FavoritesCarousel';
import { Virtuoso } from 'react-virtuoso';
import ProfileTopBar from './components/ProfileTopBar';
import ViewRun from '../../components/ViewRun';
import VideoPlayerView from '../ViewRunPage/VideoPlayerView';
import { RunEditContextProvider } from '../ViewRunPage/index';
import UnanalyzedRunsList from './components/UnanalyzedRuns';
import HStack from 'components/HStack';
import { Trans, useTranslation } from 'react-i18next';
import { useAuthService } from 'services/Authentication';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';
import FirmwareModal from 'components/modals/FirmwareModal';

// Helper function for comparing RunModels by ID
const equalId = (a: RunModel, b: RunModel) => a.id === b.id;

type EditModes = 'rider' | 'skateboard' | 'settings' | 'filter';
const ProfileTab: FunctionComponent<{}> = observer(() => {
  const fsm = useFSM();
  const api = useCalculationsAPI();
  const { currentUser, recordList } = useStores(); // Added recordList
  const storage = useCloudStorage(); // Initialized storage
  const { id: selectedRunId } = useParams<{ id?: string }>();
  const history = useHistory();
  
  // Check if running as web app
  const isWeb = process.env.REACT_APP_IS_WEB === 'true';
  
  // State for video player when in web mode
  const [videoActive, setVideoActive] = useState(false);
  const [isLoading3D, setIsLoading3D] = useState(false);
  
  const [debugMode, setDebugMode] = useState(false);
  const [_toast, _dismiss] = useIonToast();
  const [, _incDebugCounter] = useCounter(5, () => {
    if (currentUser?.isAdmin || isInDebugMode()) setDebugMode(!debugMode);
  });
  const [editUser, setEditUser] = useState<SnapshotOut<UserSN> | undefined>(
    undefined
  );
  const [editMode, setEditMode] = useState<EditModes | null>(null);
  const [hasActiveLicense, setHasActiveLicense] = useState<boolean>();
  const [present] = useIonToast();
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [showFirmwareTestModal, setShowFirmwareTestModal] = useState(false);
  const { t } = useTranslation();
  const firmwareUpdate = useFirmwareUpdate();

  const AuthService = useAuthService();
  const reallyDeleteUser = async () => {
    try {
      await api.deleteUser();
      AuthService.signOut();
    } catch (err) {
      present({
        message: t('ERROR_DELETING_USER', {
          defaultValue:
            'Nutzer konnte nicht gelöscht werden. Bitte wende dich an den Support!',
        }),
        color: 'danger',
        position: MESSAGE_POSITION,
        duration: MESSAGE_ALIVE_MS,
      });
      return;
    }
  };

  const deleteUserConfirm = () => {
    return present({
      header: t('DELETE_ACCOUNT_CONFIRM'),
      position: MESSAGE_POSITION,
      color: 'danger',
      buttons: [
        { text: t('CANCEL'), role: 'cancel' },
        {
          text: t('DELETE', { defaultValue: 'Löschen' }),
          handler: reallyDeleteUser,
        },
      ],
    });
  };

  const deleteUser = () => {
    present({
      header: t('DELETE_ACCOUNT_HEADER'),
      message: t('DELETE_ACCOUNT_TEXT'),
      position: MESSAGE_POSITION,
      color: 'danger',
      buttons: [
        { text: t('CANCEL'), role: 'cancel' },
        {
          text: t('DELETE', { defaultValue: 'Löschen' }),
          handler: deleteUserConfirm,
          role: 'destructive',
        },
      ],
    });
  };

  // const checkLicense = useCallback(async () => {
  //   if (!currentUser?.license) return;
  //   await api.checkKeyStatus(currentUser.license);
  // }, [api, currentUser?.license]);

  const checkLicense = useCallback(async () => {
    if (!currentUser?.license) return;
    const response: KeyStatusResponse = await api.checkKeyStatus(
      currentUser.license
    );
    setHasActiveLicense(response.text === 'active');
  }, [setHasActiveLicense, api, currentUser?.license]);

  useEffect(() => {
    checkLicense().catch(() => {
      setTimeout(checkLicense, 1000); // retry in a second!
    });
  }, [currentUser?.license, checkLicense]);

  useIonViewDidEnter(() => {
    checkLicense().catch(() => {
      setTimeout(checkLicense, 1000); // retry in a second!
    });
  });

  const oldResizeModeRef = useRef<any>(null);
  useIonViewDidEnter(() => {
    Keyboard.setAccessoryBarVisible({ isVisible: true }).catch(() => {});
    Keyboard.setScroll({ isDisabled: false }).catch(() => {});
    Keyboard.getResizeMode()
      .then((mode) => {
        oldResizeModeRef.current = mode;
      })
      .catch(() => {});
    Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
  });
  useIonViewDidLeave(() => {
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
    Keyboard.setResizeMode(oldResizeModeRef.current).catch(() => {});
  });

  // Alle anderen Hooks und Berechnungen hier...
  const startEditMode = () => {
    setEditUser(_.cloneDeep(getSnapshot(currentUser || {})));
  };
  const isEditMode = !!editUser;

  const commitEdit = async () => {
    if (!editUser || !currentUser) return;
    await currentUser.changeInfo(editUser);
    currentUser.putSelf();
    setEditUser(undefined);
  };

  const abortEdit = () => {
    setEditUser(undefined);
  };

  const bleConnected = fsm.bleState === 'CONNECTED';
  const theUser = editUser ?? currentUser;

  // Memoized trick lists for the current user
  const items: Array<RunModel> = (() => {
    let ret: Array<RunModel> = [];
    ret = _.uniqWith(recordList.tricks, equalId);
    return _.orderBy(ret, 'date', 'desc');
  })();

  const favorites: Array<RunModel> = useMemo(() => {
    return recordList.favorites;
  }, [recordList.favorites]);

  // Find selected run based on ID from URL
  const selectedRun = useMemo(() => {
    if (!selectedRunId) return null;
    return items.find((item) => item.id === selectedRunId) || null;
  }, [selectedRunId, items]);

  // Helper function to check if run has video
  const hasVideo = useCallback((run: RunModel) => {
    // Check if video exists locally or can be downloaded
    return !!(run.localVideoUri || run.videoUrl);
  }, []);

  // Effect to auto-download videos when selectedRun changes
  useEffect(() => {
    if (selectedRun && selectedRun.videoUrl && !selectedRun.localVideoUri) {
      selectedRun.downloadVideo().catch((error) => {
        console.warn('Failed to download video for selected run:', error);
      });
    }
  }, [selectedRun]);

  // Handler for clicking on a run item in web mode
  const handleRunClick = useCallback(
    (run: RunModel) => {
      if (isWeb) {
        // Show loading state when switching runs
        setIsLoading3D(true);
        // In web mode, just update the URL without reloading
        history.replace(`/profile/${run.id}`);
      }
      // In mobile mode, the default navigation will handle it
    },
    [isWeb, history]
  );

  // Handler for video toggle
  const toggleVideoView = useCallback(() => {
    setVideoActive(!videoActive);
  }, [videoActive]);

  // Edit context for ViewRun component (read-only in profile view)
  const editContext = useMemo(
    () => ({
      isEdit: false,
      onSectionRename: () => {}, // No-op in profile view
      startEditMode: () => {}, // No-op in profile view
    }),
    []
  );

  // Handle loading state when selectedRun changes
  useEffect(() => {
    if (selectedRun && isLoading3D) {
      // Give ViewRun components a moment to initialize
      const timer = setTimeout(() => {
        setIsLoading3D(false);
      }, 1000); // Adjust timing as needed
      
      return () => clearTimeout(timer);
    }
    
    // If no selectedRun or not loading, ensure loading is false
    setIsLoading3D(false);
    return; // Explicit return for TypeScript
  }, [selectedRun, isLoading3D]);

  const [presentDeleteAlert] = useIonAlert();

  // State for toggling the display of past runs

  const onDeletePlay = async (trick: RunModel) => {
    if (!storage) {
      present({
        message: 'Storage not available. Please check your connection.',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
      return;
    }

    // Show confirmation dialog
    presentDeleteAlert({
      header: 'Figur löschen?',
      message:
        'Möchten Sie diese Figur wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      buttons: [
        {
          text: 'Abbrechen',
          role: 'cancel',
        },
        {
          text: 'Löschen',
          role: 'destructive',
          handler: async () => {
            try {
              const success = recordList.deleteRun(trick);

              if (success) {
                present({
                  message: 'Run erfolgreich gelöscht.',
                  duration: MESSAGE_ALIVE_MS,
                  color: 'success',
                  position: MESSAGE_POSITION,
                });
              } else {
                present({
                  message: 'Run konnte nicht gelöscht werden.',
                  duration: MESSAGE_ALIVE_MS,
                  color: 'danger',
                  position: MESSAGE_POSITION,
                });
              }
            } catch (error) {
              present({
                message: 'Fehler beim Löschen ders Runs.',
                duration: MESSAGE_ALIVE_MS,
                color: 'danger',
                position: MESSAGE_POSITION,
              });
            }
          },
        },
      ],
    });
  };

  const editRider = () => {
    setEditMode('rider');
    startEditMode();
  };

  const editSkateboard = () => {
    setEditMode('skateboard');
    startEditMode();
  };

  const openSettings = () => {
    setEditMode('settings');
    startEditMode();
  };

  // Frühe Returns nach allen Hooks
  if (showDebugModal) {
    return <DebugModal onDismiss={() => setShowDebugModal(false)} />;
  }

  if (debugMode) {
    return <DebugModal onDismiss={() => setDebugMode(false)} />;
  }

  if (!currentUser) return null; // User is required!
  if (!theUser) return null; // Ensure theUser is available

  return (
    <IonPage className={isWeb ? 'web-layout' : ''}>
      <RiderEditModal
        isOpen={isEditMode && editMode === 'rider'}
        onDidDismiss={abortEdit}
        onCommit={commitEdit}
        user={editUser}
        setUser={setEditUser}
      />
      <BoardEditModal
        isOpen={isEditMode && editMode === 'skateboard'}
        onDidDismiss={abortEdit}
        onCommit={commitEdit}
        user={editUser}
      />
      <SettingsModal
        isOpen={isEditMode && editMode === 'settings'}
        onDidDismiss={abortEdit}
        onCommit={commitEdit}
      />
      {!isWeb && (
        <Toolbar
          bleConnected={bleConnected}
          editRider={editRider}
          editBoard={editSkateboard}
          openSettings={openSettings}
          onDeleteUser={deleteUser}
          onOpenDebugModal={() => setShowDebugModal(true)}
          onOpenFirmwareTest={() => setShowFirmwareTestModal(true)}
        />
      )}
      <IonContent>
        {isWeb ? (
          // Web layout: Two-column with 3D view
          <div className="profile-main-container">
            {/* Left column for profile content */}
            <div className="profile-left-column">
              <Toolbar
                bleConnected={bleConnected}
                editRider={editRider}
                editBoard={editSkateboard}
                openSettings={openSettings}
                onDeleteUser={deleteUser}
                onOpenDebugModal={() => setShowDebugModal(true)}
                onOpenFirmwareTest={() => setShowFirmwareTestModal(true)}
              />
              <div style={{ marginBottom: 16 }} />
              <div
                className="profile-topbar-wrapper"
                style={{ marginLeft: 16 }}
              >
                <ProfileTopBar onclick={_incDebugCounter} user={currentUser} />
              </div>
              {/* Figure List direkt darunter */}
              <div className="favorites-carousel-wrapper">
                <FavoritesCarousel
                  favorites={favorites}
                  onClick={isWeb ? handleRunClick : undefined}
                />
              </div>
              {/* <UnanalyzedRunsList runs={unAnalyzedRuns} onDelete={onDeletePlay} /> */}

              <div
                id="favorites"
                className="section-container"
                style={{ marginTop: '16px' }}
              >
                <div className="profile-pastruns-header">
                  <IonListHeader className="section-header">
                    <IonIcon
                      src={`/assets/icons/TrickSpikeIcon.svg`}
                      style={{ width: '0.85em', marginLeft: '8px' }}
                    />
                    <Trans i18nKey="PAST_RUNS">Runs</Trans>
                    <span
                      className="section-count"
                      style={{ color: 'var(--ion-color-primary)' }}
                    >
                      ({items.length})
                    </span>
                  </IonListHeader>
                </div>
              </div>
              <Virtuoso
                totalCount={items.length}
                itemContent={(index: number) => {
                  const item = items[index];
                  return (
                    <div
                      className={`profile-runlist-item-container ${
                        isWeb && selectedRunId === item.id ? 'selected' : ''
                      }`}
                    >
                      <div className="profile-runlist-item-inner">
                        <RunListItem
                          key={`public_${item.id}_${index}`}
                          item={item}
                          onDeletePlay={onDeletePlay}
                          isPublic={false}
                          isFriend={false}
                          directNavigation={!isWeb}
                          onClick={isWeb ? handleRunClick : undefined}
                        />
                      </div>
                    </div>
                  );
                }}
              />
            </div>

            {/* Right column split into 3D view and video */}
            <div className="profile-right-column">
              {selectedRun ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                  }}
                >
                  {/* Loading overlay for 3D area only */}
                  {isLoading3D && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1001,
                        backdropFilter: 'blur(2px)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <IonSpinner
                          name="crescent"
                          style={{
                            width: '48px',
                            height: '48px',
                            '--color': 'var(--ion-color-primary)',
                          }}
                        />
                        <div
                          style={{
                            color: 'var(--ion-color-primary)',
                            fontSize: '16px',
                            fontWeight: '500',
                          }}
                        >
                          Loading 3D View...
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Check if video exists and is available */}
                  {hasVideo(selectedRun) ? (
                    <>
                      {/* 3D View - takes 50% of right column when video exists */}
                      <div
                        style={{
                          width: '50%',
                          height: '100%',
                          position: 'relative',
                        }}
                      >
                        <RunEditContextProvider value={editContext}>
                          <ViewRun
                            record={selectedRun}
                            hidden={false}
                            videoActive={false}
                            onVideoToggle={toggleVideoView}
                            hasVideo={hasVideo(selectedRun)}
                            showRunStatsBox={true}
                            showFigurePopup={false}
                            showGForceDisplay={false}
                          />
                        </RunEditContextProvider>
                      </div>
                      
                      {/* Video Player - takes 50% of right column */}
                      <div
                        style={{
                          width: '50%',
                          height: '100%',
                          position: 'relative',
                          borderLeft: '1px solid var(--ion-color-medium)',
                        }}
                      >
                        <RunEditContextProvider value={editContext}>
                          <VideoPlayerView
                            videoActive={true}
                            onVideoToggle={toggleVideoView}
                            hasVideo={hasVideo(selectedRun)}
                            record={selectedRun}
                            hidden={false}
                            showRunStatsBox={false}
                            showFigurePopup={false}
                            showGForceDisplay={false}
                          />
                        </RunEditContextProvider>
                      </div>
                    </>
                  ) : (
                    /* 3D View only - takes full width when no video */
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                      }}
                    >
                      <RunEditContextProvider value={editContext}>
                        <ViewRun
                          record={selectedRun}
                          hidden={false}
                          videoActive={false}
                          onVideoToggle={toggleVideoView}
                          hasVideo={false}
                          showRunStatsBox={false}
                          showFigurePopup={false}
                          showGForceDisplay={false}
                        />
                      </RunEditContextProvider>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: 'var(--ion-color-primary)',
                    color: 'var(--ion-color-primary-contrast)',
                    fontSize: '18px',
                    fontWeight: '500',
                  }}
                >
                  Select a run to view details
                </div>
              )}
            </div>
          </div>
        ) : (
          // Mobile layout: Single column
          <>
            <div style={{ marginBottom: 16 }} />
            <div className="profile-topbar-wrapper">
              <ProfileTopBar onclick={_incDebugCounter} user={currentUser} />
            </div>
            {/* Figure List direkt darunter */}
            <div className="favorites-carousel-wrapper">
              <FavoritesCarousel
                favorites={favorites}
                onClick={isWeb ? handleRunClick : undefined}
              />
            </div>
            {/* <UnanalyzedRunsList runs={unAnalyzedRuns} onDelete={onDeletePlay} /> */}

            <div
              id="favorites"
              className="section-container"
              style={{ marginTop: '16px' }}
            >
              <div className="profile-pastruns-header">
                <IonListHeader className="section-header">
                  <IonIcon
                    src={`/assets/icons/TrickSpikeIcon.svg`}
                    style={{ width: '0.85em', marginLeft: '8px' }}
                  />
                  <Trans i18nKey="PAST_RUNS">Runs</Trans>
                  <span
                    className="section-count"
                    style={{ color: 'var(--ion-color-primary)' }}
                  >
                    ({items.length})
                  </span>
                </IonListHeader>
              </div>
            </div>
            <Virtuoso
              totalCount={items.length}
              itemContent={(index: number) => {
                const item = items[index];
                return (
                  <div
                    className={`profile-runlist-item-container ${
                      isWeb && selectedRunId === item.id ? 'selected' : ''
                    }`}
                  >
                    <div className="profile-runlist-item-inner">
                      <RunListItem
                        key={`public_${item.id}_${index}`}
                        item={item}
                        onDeletePlay={onDeletePlay}
                        isPublic={false}
                        isFriend={false}
                        directNavigation={!isWeb}
                        onClick={isWeb ? handleRunClick : undefined}
                      />
                    </div>
                  </div>
                );
              }}
            />
          </>
        )}

        {/* Firmware Test Modal */}
        {showFirmwareTestModal && (
          <FirmwareModal
            isOpen={showFirmwareTestModal}
            onClose={() => setShowFirmwareTestModal(false)}
            firmwareUpdate={firmwareUpdate}
          />
        )}
      </IonContent>
    </IonPage>
  );
});

export default ProfileTab;
